/**
 * Apply a feed-scope patch requested by Kai (via the `apply_feed_filters` SSE
 * event) to the shared `FeedContext` state. Mirrors the bookings
 * `bookingTableFilters.ts` pattern: normalise the patch, persist to
 * sessionStorage, and notify the mounted `FeedProvider` via a window event.
 *
 * The feed uses a virtual-scope model (a `scope_dimension` + drill-down
 * `scope_key` plus optional `time_horizon` / `severity`) rather than PropertyFilter
 * tokens, so this is a smaller, separate utility from the bookings one.
 */
import type { FeedFilters } from "@/context/FeedContext";
import { FEED_FILTER_EVENT_TYPES } from "./feedVisuals";
import { FEED_PAGE_SIZE } from "@/hooks/feedPagination";
import type {
  FeedEventType,
  FeedListParams,
  FeedSeverity,
  ScopeDimension,
  TimeHorizon,
} from "@/types/feed";

export const KAI_FEED_FILTERS_EVENT = "kai-feed-filters";

/** Shared sessionStorage key with `FeedContext` (`twido:feed:filters`). */
const FEED_FILTERS_STORAGE_KEY = "twido:feed:filters";

const DEFAULT_FILTERS: FeedFilters = {
  scopeDimension: "my",
  reviewStatus: "active",
};

const SCOPE_DIMENSIONS: ScopeDimension[] = [
  "my",
  "booking",
  "customer",
  "sales_person",
  "operator",
  "agent",
  "carrier",
  "lane",
  "management",
];

const TIME_HORIZONS: TimeHorizon[] = ["immediate", "developing", "strategic"];

const SEVERITIES: FeedSeverity[] = ["info", "warning", "urgent"];

const EVENT_TYPES: FeedEventType[] = [
  ...FEED_FILTER_EVENT_TYPES,
  "rebook_confirm",
  "rebook_progress",
  "commercial_digest",
];

/** Dimensions that carry a drill-down `scope_key`. */
const KEYED_DIMENSIONS = new Set<ScopeDimension>([
  "booking",
  "customer",
  "sales_person",
  "operator",
  "agent",
  "carrier",
  "lane",
]);

/** The validated feed patch Kai emits via `apply_feed_filters`. */
export interface KaiFeedFiltersPatch {
  mode?: "merge" | "replace" | "clear";
  scope_dimension?: string;
  scope_key?: string;
  time_horizon?: string;
  severity?: string;
  event_type?: string;
}

export interface KaiFeedFiltersEventDetail {
  filters: FeedFilters;
}

function isScopeDimension(value: unknown): value is ScopeDimension {
  return (
    typeof value === "string" &&
    (SCOPE_DIMENSIONS as string[]).includes(value)
  );
}

function isTimeHorizon(value: unknown): value is TimeHorizon {
  return (
    typeof value === "string" && (TIME_HORIZONS as string[]).includes(value)
  );
}

function isSeverity(value: unknown): value is FeedSeverity {
  return typeof value === "string" && (SEVERITIES as string[]).includes(value);
}

function isEventType(value: unknown): value is FeedEventType {
  return typeof value === "string" && (EVENT_TYPES as string[]).includes(value);
}

export function loadFeedFilters(): FeedFilters {
  try {
    const raw = sessionStorage.getItem(FEED_FILTERS_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as FeedFilters;
      if (isScopeDimension(parsed.scopeDimension)) {
        const eventType =
          parsed.eventType &&
          FEED_FILTER_EVENT_TYPES.includes(parsed.eventType)
            ? parsed.eventType
            : undefined;
        return {
          ...parsed,
          eventType,
          reviewStatus: parsed.reviewStatus ?? "active",
        };
      }
    }
  } catch {
    // Ignore malformed session state and fall back to the default.
  }
  return { ...DEFAULT_FILTERS };
}

/**
 * Deep-link (`?feed_scope` / `?feed_key`) wins over the session snapshot so a
 * hover-prefetch / splash warm uses the same query the page will mount with.
 */
export function resolveFeedFilters(): FeedFilters {
  if (typeof window === "undefined") return { ...DEFAULT_FILTERS };
  try {
    const search = window.location?.search;
    if (search) {
      const url = new URLSearchParams(search);
      const urlDim = url.get("feed_scope");
      if (isScopeDimension(urlDim)) {
        return {
          scopeDimension: urlDim,
          scopeKey: url.get("feed_key") ?? undefined,
          reviewStatus: "active",
        };
      }
    }
  } catch {
    // Tests / non-browser shells may not expose location.
  }
  return loadFeedFilters();
}

/**
 * Count of overlays that live in the extra-filters rail / sheet.
 * Review status stays on the page, so it is not included.
 */
export function countExtraFeedFilters(filters: FeedFilters): number {
  let count = 0;
  if (filters.scopeDimension !== "my") count += 1;
  if (filters.timeHorizon) count += 1;
  if (filters.severity) count += 1;
  if (filters.eventType) count += 1;
  if (filters.favoritesOnly) count += 1;
  return count;
}

/** Drop sheet overlays while leaving Active / All / Reviewed as-is. */
export function clearExtraFeedFilters(filters: FeedFilters): FeedFilters {
  return {
    ...filters,
    scopeDimension: "my",
    scopeKey: undefined,
    timeHorizon: undefined,
    severity: undefined,
    eventType: undefined,
    favoritesOnly: undefined,
  };
}

export interface FeedListParamsOptions {
  /**
   * When false and the scope is `booking`, request the tenant-wide timeline
   * for that booking (mirrors booking detail when "show only my bookings" is off).
   */
  intersectPortfolio?: boolean;
}

/** Shared `GET /v1/feed` query for the rail, the page, and prefetch. */
export function feedFiltersToListParams(
  filters: FeedFilters,
  limit: number = FEED_PAGE_SIZE,
  options?: FeedListParamsOptions,
): FeedListParams {
  const params: FeedListParams = { limit };
  if (filters.scopeDimension && filters.scopeDimension !== "my") {
    params.scope_dimension = filters.scopeDimension;
  }
  if (filters.scopeKey) params.scope_key = filters.scopeKey;
  if (
    filters.scopeDimension === "booking" &&
    filters.scopeKey &&
    options?.intersectPortfolio === false
  ) {
    params.portfolio_intersection = false;
  }
  if (filters.timeHorizon) params.time_horizon = filters.timeHorizon;
  if (filters.severity) params.severity = filters.severity;
  if (filters.eventType) params.event_type = filters.eventType;
  if (filters.reviewStatus && filters.reviewStatus !== "active") {
    params.review_status = filters.reviewStatus;
  }
  if (filters.favoritesOnly) {
    params.favorites_only = "true";
  }
  const search = filters.searchText?.trim();
  if (search) {
    params.search = search;
  }
  return params;
}

function persistFeedFilters(filters: FeedFilters) {
  try {
    sessionStorage.setItem(FEED_FILTERS_STORAGE_KEY, JSON.stringify(filters));
  } catch {
    // Best-effort persistence only; the window event still applies the change.
  }
}

/** Merge/replace/clear a Kai patch into a feed-filter model. */
export function applyKaiFeedFiltersPatch(
  current: FeedFilters,
  patch: KaiFeedFiltersPatch,
): FeedFilters {
  const mode = patch.mode ?? "merge";
  if (mode === "clear") return { ...DEFAULT_FILTERS };

  const next: FeedFilters =
    mode === "replace" ? { ...DEFAULT_FILTERS } : { ...current };

  if (isScopeDimension(patch.scope_dimension)) {
    next.scopeDimension = patch.scope_dimension;
    // Switching to a keyless dimension drops any stale drill-down key.
    if (!KEYED_DIMENSIONS.has(patch.scope_dimension)) {
      next.scopeKey = undefined;
    }
  }

  const scopeKey = patch.scope_key?.trim();
  if (scopeKey && KEYED_DIMENSIONS.has(next.scopeDimension)) {
    next.scopeKey = scopeKey;
  }

  if (isTimeHorizon(patch.time_horizon)) {
    next.timeHorizon = patch.time_horizon;
  }

  if (isSeverity(patch.severity)) {
    next.severity = patch.severity;
  }

  if (isEventType(patch.event_type)) {
    next.eventType = patch.event_type;
  }

  return next;
}

/**
 * Apply a Kai feed patch to the stored filters, persist, and notify the mounted
 * `FeedProvider` via a window event so the feed refetches + resubscribes.
 */
export function applyKaiFeedFilters(patch: KaiFeedFiltersPatch): FeedFilters {
  const next = applyKaiFeedFiltersPatch(loadFeedFilters(), patch);
  persistFeedFilters(next);
  window.dispatchEvent(
    new CustomEvent<KaiFeedFiltersEventDetail>(KAI_FEED_FILTERS_EVENT, {
      detail: { filters: next },
    }),
  );
  return next;
}
